import React, { useState } from "react";
import { Alert, Box, Link as MuiLink, Stack, Typography } from "@mui/material";
import AuthForm from "../../components/AuthForm"; // Reusable form component
import { signIn, confirmSignIn } from "aws-amplify/auth"; // Import only the necessary functions
import { Link as RouterLink, useNavigate, useLocation } from "react-router-dom";
import backgroundImage from "../../images/Sign-In-Background.jpg";
import { AppButton } from "../../components/ui";
import AuthSplitPage from "./AuthSplitPage";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isNewPasswordRequired, setIsNewPasswordRequired] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";
  const accountMessage = location.state?.accountMessage || null;

  const handleSignIn = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await signIn({
        username: email,
        password,
        options: {
          authFlowType: "USER_PASSWORD_AUTH", // Explicit auth flow
        },
      });

      if (
        response?.nextStep?.signInStep ===
        "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
      ) {
        setIsNewPasswordRequired(true);
      } else {
        console.log("User signed in successfully:", response);
        navigate(from); // Redirect after successful login
      }
    } catch (err) {
      console.error("Error during sign-in:", err);
      setError(err.message || "An unexpected error occurred.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetNewPassword = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await confirmSignIn({
        challengeResponse: newPassword,
      });
      console.log("Password updated successfully:", response);
      navigate(from); // Redirect after successful password update
    } catch (err) {
      console.error("Error setting new password:", err);
      setError(err.message || "An unexpected error occurred.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthSplitPage
      heroBackgroundImage={backgroundImage}
      heroCardMaxWidth={600}
      formFirstOnMobile={false}
      hero={
        <Box>
          <Stack spacing={{ xs: 2, sm: 3 }}>
            <Typography variant="h1" component="h1" gutterBottom>
              Welcome back to 10k Science!
            </Typography>
            <Typography variant="body1" gutterBottom>
              Access your account and continue your journey.
            </Typography>
          </Stack>
          <Typography variant="body2" color="textSecondary" sx={{ mt: { xs: 2, sm: 3 } }}>
            If you&apos;re new here, <a href="/signup">sign up</a> and get started
            for free.
          </Typography>
        </Box>
      }
      form={
        <Box sx={{ display: "grid", gap: 2 }}>
          {accountMessage && <Alert severity="success">{accountMessage}</Alert>}
          {error && <Alert severity="error">{error}</Alert>}
          {!isNewPasswordRequired ? (
            <>
              <AuthForm
                title="Sign In"
                submitLabel="Sign In"
                maxWidth={400}
                fields={[
                  {
                    label: "Email",
                    type: "text",
                    value: email,
                    onChange: (e) => setEmail(e.target.value),
                  },
                  {
                    label: "Password",
                    type: "password",
                    value: password,
                    onChange: (e) => setPassword(e.target.value),
                  },
                ]}
                onSubmit={handleSignIn}
                isLoading={isLoading}
                footerAction={
                  <AppButton
                    kind="ghost"
                    onClick={() => navigate("/reset-password")}
                  >
                    Forgot Password?
                  </AppButton>
                }
              />
              <Typography variant="body2" color="text.secondary" align="center">
                Don&apos;t have an account?{" "}
                <MuiLink
                  component={RouterLink}
                  to="/signup"
                  underline="hover"
                  sx={{
                    fontWeight: 600,
                    color: "var(--color-action-primary, #239fc6)",
                  }}
                >
                  Sign up
                </MuiLink>
              </Typography>
            </>
          ) : (
            <AuthForm
              title="Set New Password"
              submitLabel="Update Password"
              maxWidth={400}
              fields={[
                {
                  label: "New Password",
                  type: "password",
                  value: newPassword,
                  onChange: (e) => setNewPassword(e.target.value),
                },
              ]}
              onSubmit={handleSetNewPassword}
              isLoading={isLoading}
            />
          )}
        </Box>
      }
      heroPanelSx={{
        backgroundAttachment: { xs: "scroll", lg: "fixed" },
      }}
      formContentMaxWidth={400}
    />
  );
};

export default LoginPage;
